import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import { TLeaderboardUser } from '../../stores';
import {
  ButtonWithShadow,
  TButtonWithShadow,
} from '../Buttons/ButtonWithShadow';
import theme, { TMaterialStyles, TShadow } from '../../theme';
import { Icon } from '../Icon';
import TypographyWithShadow from '../Typography/TypographyWithShadow';
import { useTranslation } from 'react-i18next';
import { formatNumberWithCommas } from '../../helpers';
import Image from '../Common/Image';

interface ILeaderboardUserItem {
  user: TLeaderboardUser;
  type?: 'DEFAULT' | 'CURRENT_USER';
}

export const LeaderboardUserItem: React.FC<ILeaderboardUserItem> = ({
  user,
  type = 'DEFAULT',
}) => {
  const { t } = useTranslation();

  const buttonType: TButtonWithShadow = useMemo(() => {
    switch (user.rank) {
      case 1:
        return 'GOLD';
      case 2:
        return 'SILVER';
      case 3:
        return 'BRONZE';
      default:
        return 'PRIMARY';
    }
  }, [user.rank]);

  const typographyShadowType: TShadow = useMemo(() => {
    switch (user.rank) {
      case 1:
        return 'GOLD';
      case 2:
        return 'SILVER';
      case 3:
        return 'BRONZE';
      default:
        return 'DARK';
    }
  }, [user.rank]);

  return (
    <ButtonWithShadow
      type={buttonType}
      sx={
        type === 'CURRENT_USER'
          ? materialStyles.itemCurrentUserContainer
          : materialStyles.itemContainer
      }
      disabled
    >
      <Box sx={materialStyles.userIconContainer}>
        <Icon sx={materialStyles.userIcon} iconName="UserIcon" />
        {user.avatar_url ? (
          <Image
            src={user.avatar_url}
            alt="avatar"
            sx={materialStyles.userAvatar}
          />
        ) : null}
      </Box>
      <Box sx={materialStyles.userInfoContainer}>
        <TypographyWithShadow
          sx={materialStyles.userNameText}
          shadowType={typographyShadowType}
        >
          {type === 'DEFAULT' ? user.userName : t('common.you')}
        </TypographyWithShadow>
        <TypographyWithShadow
          sx={materialStyles.userPointsText}
          shadowType={typographyShadowType}
        >
          {t('common.points_with_count', {
            points: formatNumberWithCommas(user.points),
          })}
        </TypographyWithShadow>
      </Box>
      <TypographyWithShadow
        sx={materialStyles.userRankText}
        shadowType={typographyShadowType}
      >{`#${user.rank}`}</TypographyWithShadow>
    </ButtonWithShadow>
  );
};

const materialStyles: TMaterialStyles = {
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    py: '9px',
    px: '16px',
    width: '100%',
    borderRadius: '14px',
  },
  itemCurrentUserContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    py: '20px',
    px: '16px',
    width: '100%',
    borderRadius: '14px',
  },
  userIconContainer: {
    position: 'relative',
    width: '43px',
    height: '43px',
    borderRadius: '10px',
    backgroundColor: theme.palette.grey[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  userAvatar: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  userIcon: {
    width: '25px',
    height: '30px',
  },
  userInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    ml: '13px',
    overflow: 'hidden',
  },
  userNameText: {
    fontWeight: 600,
    textAlign: 'left',
    fontSize: '19px',
    lineHeight: '100%',
    mb: '3px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userPointsText: {
    fontWeight: 600,
    textAlign: 'left',
    fontSize: '12px',
    lineHeight: '100%',
  },
  userRankText: {
    fontFamily: 'Rubber Duck !important',
    fontSize: '22px',
    lineHeight: '100%',
  },
};
